import { useRef } from "react";
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "framer-motion";
import { Sparkle } from "lucide-react";

const wrap = (min, max, v) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

function VelocityRow({ children, baseVelocity = 2, className = "" }) {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smooth = useSpring(scrollVelocity, { damping: 50, stiffness: 400 });
  const factor = useTransform(smooth, [0, 1000], [0, 5], { clamp: false });
  const skewX = useTransform(smooth, [-1200, 1200], [10, -10]);
  const x = useTransform(baseX, (v) => `${wrap(-25, 0, v)}%`);
  const dir = useRef(1);

  useAnimationFrame((_, delta) => {
    let move = dir.current * baseVelocity * (delta / 1000);
    const f = factor.get();
    if (f < 0) dir.current = -1;
    else if (f > 0) dir.current = 1;
    move += dir.current * move * Math.abs(f);
    baseX.set(baseX.get() + move);
  });

  return (
    <div className="overflow-hidden whitespace-nowrap">
      <motion.div style={{ x, skewX }} className={`flex whitespace-nowrap ${className}`}>
        {[0, 1, 2, 3].map((k) => (
          <span key={k} className="flex items-center shrink-0">
            {children}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

const Phrase = ({ text, outline }) => (
  <span className="flex items-center">
    <span className={`font-serif italic text-3xl md:text-5xl px-6 md:px-10 ${outline ? "text-stroke-gold" : "gold-text"}`}>
      {text}
    </span>
    <Sparkle size={14} className="text-gold-light shrink-0" />
  </span>
);

export default function EditorialMarquee() {
  return (
    <div
      data-testid="editorial-marquee"
      style={{ perspective: 900 }}
      className="relative border-b border-gold/20 bg-obsidian py-8 md:py-10 overflow-hidden"
    >
      <div style={{ transform: "rotateX(10deg)", transformStyle: "preserve-3d" }} className="space-y-3">
        <VelocityRow baseVelocity={-1.6}>
          <Phrase text="A Creation of Around The World Ltd" />
          <Phrase text="Around The World" outline />
        </VelocityRow>
        <VelocityRow baseVelocity={1.6}>
          <Phrase text="Perfumes · Watches · Tailoring · Belts · Candles" outline />
          <Phrase text="One Name. One Standard." />
        </VelocityRow>
      </div>
    </div>
  );
}
